import { Affix, Button } from 'antd';
import classes from './RightSideBar.module.scss';
import Sider from 'antd/es/layout/Sider';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setIsRSidebarCollapsed } from 'store/slices/windowStateSlice';
import Assests from 'assets/icons';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { LeftOutlined } from '@ant-design/icons';
import { setUserChoosen } from 'store/slices/dataUsersSlice';
import MoreInfoComponent from 'components/SideBar/UsersTab/MoreInfoComponent';
import FileSettings from './Components/FileSettings/FileSettings';
import RoomSettings from './Components/RoomSettings/RoomSettings';
import RolesSettings from './Components/RolesSettings/RolesSettings';
import ChatSettings from './Components/ChatSettings/ChatSettings';
import { useLocation } from 'react-router';
import PermissionsSettings from './Components/PermissionsSettings/PermissionsSettings';

export type RSidbarItem = {
  name: string;
  type?: 'folder' | 'link' | 'image' | 'text' | 'divider' | 'user';
  url?: string; // для ссылок и картинок
  children?: RSidbarItem[];
  icon?: React.ReactNode;
  count?: number; 
  timestamp?: string;
  content?: string | null;
  isRenderTimestamp?: boolean;
  exceptionKeys?: (string | number)[]
};

export type RSidebarStack = { parent: RSidbarItem | null; children: RSidbarItem[] }

const RightSideBar = () => {
  const [currentSegment, setCurrentSegment] = React.useState<'main' | 'room' | 'user'>('user');
  const [stack, setStack] = React.useState<RSidebarStack[]>([{parent: null, children: []}]);

  const { isRSidebarCollapsed, rSidebarEntity, rSidebarWidth } = useAppSelector((store) => store.windowState);
  const { userPermissions } = useAppSelector((store) => store.userData);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const location = useLocation();
  const isTariffEnded = location.pathname.endsWith('/endedTariff');
  const isAwaitingPayment = location.pathname.endsWith('/awaitingPayment');

  React.useEffect(() => {
    if((isTariffEnded || isAwaitingPayment) && !isRSidebarCollapsed){
      dispatch(setUserChoosen(null));
      dispatch(setIsRSidebarCollapsed({collapsed: true, entity: '', width: 0}))
    }
  }, [isTariffEnded, isAwaitingPayment, isRSidebarCollapsed, dispatch]);
  
  React.useEffect(() => {
    const sectionName = rSidebarEntity?.split('.')[0];
    
    if (!isRSidebarCollapsed && dataRoom && sectionName) {
      const isAvailableToEditRoles = userPermissions?.can_edit_roles || userPermissions?.can_create_group_all_roles;
      const isAvailablePermissionsSettings = userPermissions?.can_edit_folder_permissions;
      const shouldResetToRoomSettings = 
        (sectionName === 'roles' && !isAvailableToEditRoles) ||
        (sectionName === 'permissions' && !isAvailablePermissionsSettings);

      if (shouldResetToRoomSettings && rSidebarEntity.includes('.room_setting')) {
        dispatch(setIsRSidebarCollapsed({
          collapsed: false,
          entity: 'room_setting',
          width: 300,
        }));
      }
    }
  }, [dataRoom, userPermissions, rSidebarEntity, isRSidebarCollapsed, dispatch])

  const onCloseClick = () => {
    dispatch(setUserChoosen(null));
    dispatch(setIsRSidebarCollapsed({collapsed: true, entity: '', width: 0}))
  };
  // const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value);

  const goBack = () => {
    if (stack.length > 1) {
      setStack(stack.slice(0, -1));
    }
  };

  const content = {
    user_data: <MoreInfoComponent currentSegment={currentSegment} />,
    room_setting: <RoomSettings stack={stack} setStack={setStack}/>,
    chat_setting: <ChatSettings i18n={i18n} t={t} stack={stack} setStack={setStack} />,
    file_setting: <FileSettings currentKey={rSidebarEntity?.split('.')[1]} />,
    roles: <RolesSettings />,
    permissions: <PermissionsSettings />
  };

  const currentLevel = stack[stack.length - 1];

  const sectionName = React.useCallback(() => {
    return rSidebarEntity?.split('.')[0];
  },[rSidebarEntity]);
    
  const setPreviousSection = () => {
    dispatch(setIsRSidebarCollapsed({
      collapsed: isRSidebarCollapsed,
      entity: String(rSidebarEntity).split('.').splice(1,1).join(),
      width: rSidebarWidth,
    }))
  }

  return (
    <Affix>
      <Sider
        theme='light'
        width={rSidebarWidth}
        collapsedWidth={0}
        collapsed={isRSidebarCollapsed}
        className={classes.rightSideBar}
      >
        <div>
          <div className={classes.rSidebarTitle}>
            {stack.length > 1 && <Button onClick={goBack} icon={<LeftOutlined />} className={classes.backButton} />}
            <div className={classes.nameWithButton}>
              {rSidebarEntity.includes('.room_setting') && (
                <Button
                  onClick={setPreviousSection}
                  icon={<LeftOutlined style={{fontSize: 20}}/>}
                  type='text'
                  className={classes.previosSection}
                />
              )}

              <div>{currentLevel?.parent?.name || t(`rightSideBar.${sectionName()}`)}</div>
            </div>

            <Button
              className={`${!isRSidebarCollapsed && classes.closeButton}`}
              onClick={onCloseClick}
              type='text'
              icon={<Assests.CloseIcon />}
            />
          </div>

          {content[sectionName() as keyof typeof content]}
          
        </div>
      </Sider>
    </Affix>
  );
}

export default RightSideBar;
